import React from 'react';
import { Box, Typography } from '@mui/material';

const emptyPanelSx = {
  width: '100%',
  textAlign: 'center',
  py: 4,
  px: 2,
  bgcolor: '#f9fafb',
  borderRadius: '12px',
  border: '1px solid #e5e7eb',
} as const;

export type ClientsListStatus = 'all' | 'active' | 'suspended' | 'draft' | 'pending_deletion';

type ClientsTabEmptyStateProps = {
  clientStatus: ClientsListStatus;
  hasActiveFilters: boolean;
  statusTotals: {
    all?: number;
    active: number;
    suspended: number;
    drafts?: number;
    pendingDeletion?: number;
  };
};

function resolveTabEmptyCopy({
  clientStatus,
  hasActiveFilters,
}: Pick<ClientsTabEmptyStateProps, 'clientStatus' | 'hasActiveFilters'>): { title: string; description?: string } {
  if (hasActiveFilters) {
    return {
      title: clientStatus === 'draft' ? 'Drafts not found' : 'Clients not found',
      description: 'Try adjusting your search or filters.',
    };
  }

  if (clientStatus === 'pending_deletion') {
    return {
      title: 'No pending deletions',
      description: 'Clients who delete their companion-app account will appear here for 14 days.',
    };
  }

  if (clientStatus === 'suspended') {
    return { title: 'No suspended clients' };
  }

  if (clientStatus === 'active') {
    return { title: 'No active clients' };
  }

  if (clientStatus === 'draft') {
    return { title: 'No draft clients', description: 'Onboarding drafts saved for later will show up here.' };
  }

  return {
    title: 'Clients not found',
    description: 'Try adjusting your search or filters.',
  };
}

export const ClientsTabEmptyState: React.FC<ClientsTabEmptyStateProps> = (props) => {
  const { title, description } = resolveTabEmptyCopy(props);

  return (
    <Box sx={emptyPanelSx}>
      <Typography sx={{ fontSize: 15, fontWeight: 600, color: '#1f2937', lineHeight: 1.4, mb: description ? 0.75 : 0 }}>
        {title}
      </Typography>
      {description ? (
        <Typography sx={{ fontSize: 14, color: '#4a5565', lineHeight: 1.45 }}>
          {description}
        </Typography>
      ) : null}
    </Box>
  );
};
